import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import DataEntryForm from '../../components/datamanagement/DataEntryForm';
import DeTimeline from '../../components/datamanagement/DeTimeline';
import DeRightSidebar from '../../components/datamanagement/DeRightSidebar';
import { useDataEntry } from '../../queries/dataEntryQueries';

/**
 * DataEntryPage — Page wrapper for the Data Entry create / edit form.
 *
 * CREATE mode  (/business/data-entry/new)
 *   → Full-width single-column card. No timeline. No sidebar.
 *
 * EDIT mode    (/business/data-entry/:id)
 *   → 4-step timeline at top (DeTimeline)
 *   → Bootstrap 5 row: col-lg-9 form  |  col-lg-3 right sidebar (DeRightSidebar)
 *   → Sidebar has 4 action buttons. Clicking one highlights that step in the
 *     timeline AND navigates to the target action route with { state: { id } }.
 *
 * IMPORTER EDIT MODE
 *   → Timeline + right sidebar visible, left column shows a summary info panel.
 */

// ── Importer Panel — simple centered layout ───────────────────────────────────
const ImporterSummaryPanel = ({ id }) => {
    const navigate = useNavigate();
    const { data: record } = useDataEntry(id);
    const fileNo = record?.fileName || record?.fileNo || '—';

    return (
        <div
            className="h-100 d-flex flex-column align-items-center justify-content-center rounded-3"
            style={{
                background: 'linear-gradient(160deg, #eef2ff 0%, #f0f7ff 60%, #fff8f0 100%)',
                border: '1px solid #dbeafe',
            }}
        >


            {/* Icon */}
            <div style={{
                width: 64, height: 64, borderRadius: 16,
                background: 'linear-gradient(135deg, #1a56db18, #f9731618)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 30, marginBottom: 16,
                border: '1px solid #dbeafe',
            }}>
                📁
            </div>

            {/* Label */}
            <div style={{
                fontSize: 13, fontWeight: 600,
                color: '#f97316',
                textTransform: 'uppercase', letterSpacing: 2,
                marginBottom: 6,
            }}>
                File Number
            </div>

            {/* File No badge */}
            <div style={{
                fontSize: 32, fontWeight: 800,
                color: '#1a56db',
                background: '#fff',
                border: '1.5px solid #dbeafe',
                borderRadius: 10,
                padding: '4px 24px',
                marginBottom: 20,
                letterSpacing: 1,
            }}>
                {fileNo}
            </div>

            {/* Hint text */}
            <div style={{
                color: '#94a3b8', fontSize: 14,
                textAlign: 'center', maxWidth: 260,
                lineHeight: 1.6,
            }}>
                Use the panel on the right to view<br />
                Copy Document, Clearing Charges,<br />
                Eway, P&amp;L and File Document.
            </div>

            {/* Back button */}
            <button
                type="button"
                onClick={() => navigate('/business/data-management')}
                style={{
                    marginTop: 24,
                    padding: '8px 28px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#1a56db',
                    background: '#fff',
                    border: '1.5px solid #1a56db',
                    borderRadius: 8,
                    cursor: 'pointer',
                    letterSpacing: 0.5,
                    transition: 'background 0.15s, color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1a56db'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#1a56db'; }}
            >
                ← Back
            </button>


        </div>
    );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const DataEntryPage = () => {
    const { id } = useParams();
    const isEditMode = id && id !== 'new';
    const pageTitle = isEditMode ? 'Edit Data Entry' : 'New Data Entry';

    // Importer role — shows summary panel instead of form in edit mode
    const isImporter = sessionStorage.getItem('importer') === 'true';

    // Limited Admin — shows form but hides right sidebar and timeline
    const userRole = sessionStorage.getItem('userRole') ?? '';
    const isLimitedAdmin = userRole.trim().toLowerCase() === 'limited admin';

    // File name state (typed or loaded from DataEntryForm)
    const [fileName, setFileName] = useState('');

    // Active step — shared between timeline and right sidebar (edit mode only)
    const [activeStep, setActiveStep] = useState(1);

    // ── CREATE MODE ──────────────────────────────────────────────────────────
    if (!isEditMode) {
        return (
            <div className="d-flex flex-column h-100 overflow-hidden overflow-y-auto py-2 py-md-3 px-2 px-md-4">
                <div
                    className="d-flex flex-column flex-grow-1 mx-auto w-100"
                    style={{ maxWidth: '960px' }}
                >
                    <div className="flex-shrink-0">
                        <PageHeader title={pageTitle} rightContent={fileName} />
                    </div>
                    <div className="flex-grow-1 d-flex flex-column overflow-hidden">
                        <DataEntryForm onFileNameChange={setFileName} />
                    </div>
                </div>
            </div>
        );
    }

    // ── EDIT MODE ─────────────────────────────────────────────────────────────
    return (
        <div className="d-flex flex-column h-100 overflow-hidden overflow-y-auto py-2 py-md-3 px-2 px-md-4">
            <div
                className="d-flex flex-column flex-grow-1 mx-auto w-100"
                style={{ maxWidth: '1280px' }}
            >
                {/* ── Step Progress Timeline — hidden for Limited Admin ── */}
                {!isLimitedAdmin && (
                    <div className="flex-shrink-0 pb-1">
                        <DeTimeline activeStep={activeStep} />
                    </div>
                )}

                {/* ── Bootstrap 5 two-column row ── */}
                <div className="row g-3 flex-grow-1 overflow-hidden">

                    {/* ── Left col: summary panel for importers / form for others ── */}
                    <div className={`col-12 ${isLimitedAdmin ? '' : 'col-lg-9'} d-flex flex-column overflow-hidden`}>
                        {isImporter ? (
                            <ImporterSummaryPanel id={id} />
                        ) : (
                            <>
                                <div className="flex-shrink-0">
                                    <PageHeader title={pageTitle} rightContent={fileName} />
                                </div>
                                <div className="flex-grow-1 d-flex flex-column overflow-hidden">
                                    <DataEntryForm onFileNameChange={setFileName} />
                                </div>
                            </>
                        )}
                    </div>

                    {/* ── Right col: Action buttons — hidden for Limited Admin ── */}
                    {!isLimitedAdmin && (
                        <div className="col-12 col-lg-3">
                            <DeRightSidebar
                                id={id}
                                activeStep={activeStep}
                                onStepChange={setActiveStep}
                            />
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default DataEntryPage;
